import axios, { AxiosResponse } from 'axios';

import config from '../../config/config';
import DefaultApiLimit from '../../config/defaultLimit';

import defaultHeaders from './base/default-headers';
import getRequest from './base/get';
import postRequest from './base/post';

interface QueryParameters {
  id?: string;
  deviceId?: string;
  limit?: number;
  [key: string]: any;
}

/**
 * API service for Diagnostic Firmware management.
 */
const diagnosticFirmwareApi = {
  /**
   * Fetch the diagnostic firmware list or item by device id with pagination.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the diagnostic firmware list.
   */
  fetchDiagnosticFirmwareList: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    const url = `${config.devices.ChargersApiUrl}api/admin/v1/diagnostics/requests`;
    return getRequest(url, {
      ...parameters,
    });
  },

  /**
   * Fetch the list filters for diagnostic firmware.
   * @param {QueryParameters} [query] - The query parameters.
   * @returns {Promise<any>} A promise that resolves with list filters.
   */
  fetchDiagnosticFirmwareFilters: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/diagnostics/requests`, {
      ...query,
    });
  },

  /**
   * Export diagnostic firmware data.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the export data.
   */
  fetchDiagnosticFirmwareExport: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    const url = `${config.devices.ChargersApiUrl}api/admin/v1/diagnostics/requests/export`;
    return getRequest(url, {
      ...parameters,
    });
  },

  /**
   * Fetch diagnostic firmware by ID.
   * @param {string} id - The diagnostic log ID.
   * @returns {Promise<any>} A promise that resolves with the diagnostic detail.
   */
  fetchDiagnosticFirmwareById: (id: string): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/diagnostics/requests/${id}`);
  },

  /**
   * Download a diagnostic firmware request file as a Blob. Returns the full
   * response (not just the blob) so the caller can read the server's
   * Content-Disposition header for the file's original name.
   * @param {string} id - The diagnostic log ID.
   * @returns {Promise<AxiosResponse<Blob>>} Promise resolving with the file response.
   */
  downloadDiagnosticLog: async (id: string): Promise<AxiosResponse<Blob>> => {
    const authToken = defaultHeaders();
    const headers = {
      ...(authToken && authToken),
    };
    return axios.get(
      `${config.devices.ChargersApiUrl}api/admin/v1/diagnostics/requests/${id}/download`,
      { headers, responseType: 'blob' },
    );
  },

  /**
   * Fetch the currently pending (non-terminal) diagnostics request for a charging point, if any.
   * @param {string} chargingPointSn - The charging point serial number.
   * @returns {Promise<any>} A promise resolving with { hasPendingRequest, diagnosticsRequest }.
   */
  fetchPendingDiagnosticsRequest: (chargingPointSn: string): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/diagnostics/requests/pending`, {
      chargingPointSn,
    });
  },

  /**
   * Cancel a diagnostics request. This only marks the request record as
   * cancelled — it cannot stop a charger that is already uploading, since
   * OCPP 1.6 has no cancellation message for GetDiagnostics.
   * @param {string} requestId - The diagnostics request's _id.
   * @param {string} [reason] - Optional reason for the cancellation (max 500 chars).
   * @returns {Promise<any>} A promise resolving with the updated (cancelled) request.
   */
  cancelDiagnosticsRequest: (requestId: string, reason?: string): Promise<any> => {
    return postRequest(
      `${config.devices.ChargersApiUrl}api/admin/v1/diagnostics/requests/${requestId}/cancel`,
      reason ? { reason } : {},
    );
  },
};

export default diagnosticFirmwareApi;
